import { useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: benefit-plan-tabs subdirectory requires four levels up to reach convex/_generated/
import type { Id } from '../../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: standard convex _generated import pattern
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { EditableCell } from '@/components/insurance/EditableCell';
import {
  BedDouble,
  Scissors,
  Siren,
  Stethoscope,
  Activity,
  ShieldPlus,
  HeartPulse,
  Baby,
  Smile,
  Eye,
  Plane,
  Luggage,
  Package,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { IBenefitPlan, IBenefitSection, IBenefitItem } from '@/@types/claims';
import type { LucideIcon } from 'lucide-react'; // LOCAL_TYPE_JUSTIFIED: needed for section theme icon typing

interface IBenefitsAndLimitsTabProps {
  // LOCAL_TYPE_JUSTIFIED: component-specific props combining plan with enriched sections
  plan: IBenefitPlan & { sections: Array<IBenefitSection & { items: IBenefitItem[] }> };
}

type SectionTheme = { bg: string; text: string; Icon: LucideIcon }; // LOCAL_TYPE_JUSTIFIED: compact section color/icon theme tuple used only here
const G = (Icon: LucideIcon): SectionTheme => ({
  bg: 'bg-green-950/20 light:bg-green-50',
  text: 'text-green-400 light:text-green-700',
  Icon,
});
const B = (Icon: LucideIcon): SectionTheme => ({
  bg: 'bg-blue-950/20 light:bg-blue-50',
  text: 'text-blue-400 light:text-blue-700',
  Icon,
});
const P = (Icon: LucideIcon): SectionTheme => ({
  bg: 'bg-pink-950/20 light:bg-pink-50',
  text: 'text-pink-400 light:text-pink-700',
  Icon,
});

const THEMES: Array<[RegExp, SectionTheme]> = [
  [/inpatient/i, G(BedDouble)],
  [/surgical/i, G(Scissors)],
  [/emergency/i, G(Siren)],
  [/outpatient/i, B(Stethoscope)],
  [/physio/i, G(Activity)],
  [/preventive/i, B(ShieldPlus)],
  [/other benefit/i, G(HeartPulse)],
  [/maternity/i, P(Baby)],
  [/dental/i, B(Smile)],
  [/optical/i, G(Eye)],
  [/evacuation|repatriation|assistance/i, B(Plane)],
  [/additional|travel/i, G(Luggage)],
];

function getTheme(name: string): SectionTheme {
  return THEMES.find(([re]) => re.test(name))?.[1] ?? G(Package);
}

export function BenefitsAndLimitsTab({ plan }: IBenefitsAndLimitsTabProps) {
  const updateItem = useMutation(api.insurance.benefitItems.update);

  async function handleSave(itemId: Id<'benefitItems'>, field: string, value: string) {
    const numericFields = ['planPaysPercent', 'youPayPercent', 'waitingPeriodMonths'];
    if (numericFields.includes(field)) {
      const parsed = parseFloat(value);
      if (!isNaN(parsed)) {
        await updateItem({ itemId, [field]: parsed } as Parameters<typeof updateItem>[0]); // AS_JUSTIFIED: dynamic key spread from validated benefit item fields
      }
    } else {
      await updateItem({ itemId, [field]: value || undefined } as Parameters<typeof updateItem>[0]); // AS_JUSTIFIED: dynamic key spread from validated benefit item fields
    }
  }

  let globalIdx = 0;

  return (
    <div className="py-6 px-8">
      <div className="rounded-xl border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="w-[50px] text-[11px] font-semibold text-muted-foreground">
                ID
              </TableHead>
              <TableHead className="text-[11px] font-semibold text-muted-foreground">
                Benefit Name
              </TableHead>
              <TableHead className="w-[60px] text-center text-[11px] font-semibold text-muted-foreground">
                Plan %
              </TableHead>
              <TableHead className="w-[130px] text-[11px] font-semibold text-muted-foreground">
                Limit
              </TableHead>
              <TableHead className="w-[50px] text-center text-[11px] font-semibold text-muted-foreground">
                Wait
              </TableHead>
              <TableHead className="w-[55px] text-center text-[11px] font-semibold text-muted-foreground">
                PreAuth
              </TableHead>
              <TableHead className="w-[200px] text-[11px] font-semibold text-muted-foreground">
                Notes
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {plan.sections.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-6">
                  No benefit items defined.
                </TableCell>
              </TableRow>
            ) : (
              plan.sections.map(section => {
                const theme = getTheme(section.name);
                const SectionIcon = theme.Icon;
                return [
                  <TableRow key={`s-${section._id}`} className={cn(theme.bg, 'border-b')}>
                    <TableCell colSpan={7} className="py-2 px-4">
                      <span
                        className={cn('flex items-center gap-2 text-xs font-semibold', theme.text)}
                      >
                        <SectionIcon className="h-3.5 w-3.5" />
                        {section.name}
                      </span>
                    </TableCell>
                  </TableRow>,
                  ...section.items.map(item => {
                    globalIdx++;
                    const id = `B${String(globalIdx).padStart(3, '0')}`;
                    const isOdd = globalIdx % 2 === 0;
                    return (
                      <TableRow key={item._id} className={cn(isOdd && 'bg-muted/20')}>
                        <TableCell className="py-2 px-4 text-xs text-muted-foreground font-mono">
                          {id}
                        </TableCell>
                        <TableCell className="py-2 px-4">
                          <EditableCell
                            value={item.name}
                            onSave={v => handleSave(item._id, 'name', v)}
                            className="text-xs font-medium text-foreground"
                          />
                        </TableCell>
                        <TableCell className="py-2 px-4 text-center">
                          <span
                            className={cn(
                              'inline-flex items-center rounded-[10px] px-2 py-0.5 text-[11px] font-semibold',
                              'bg-green-950/20 light:bg-green-50 text-green-400 light:text-green-600'
                            )}
                          >
                            {item.planPaysPercent}%
                          </span>
                        </TableCell>
                        <TableCell className="py-2 px-4 text-xs text-muted-foreground">
                          <EditableCell
                            value={
                              item.limitValue ??
                              (item.annualLimit
                                ? `€${item.annualLimit.toLocaleString()}`
                                : 'Full cover')
                            }
                            onSave={v => handleSave(item._id, 'limitValue', v)}
                            className="text-xs"
                          />
                        </TableCell>
                        <TableCell className="py-2 px-4 text-center text-xs text-muted-foreground/50">
                          {item.waitingPeriodMonths ? `${item.waitingPeriodMonths} mo` : '—'}
                        </TableCell>
                        <TableCell className="py-2 px-4 text-center">
                          {item.preAuthRequired?.toLowerCase().startsWith('y') ? (
                            <span className="inline-flex items-center rounded-[10px] px-1.5 py-0.5 text-[11px] font-medium bg-red-950/20 light:bg-red-50 text-red-400 light:text-red-500">
                              Yes
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-[10px] px-1.5 py-0.5 text-[11px] font-medium bg-green-950/20 light:bg-green-50 text-green-400 light:text-green-600">
                              No
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="py-2 px-4 text-[11px] text-muted-foreground">
                          <EditableCell
                            value={item.benefitSpecificNotes ?? item.coverageConditions ?? ''}
                            onSave={v => handleSave(item._id, 'benefitSpecificNotes', v)}
                            className="text-[11px]"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  }),
                ];
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
